import { cn } from '@/lib/utils';
import { GripVertical, X } from '@music-vine/cadence/icons';
import { Button } from '@music-vine/cadence/ui';
import { AnimatePresence, motion } from 'motion/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { clearVideoAtom, videoElementAtom, videoFileAtom, videoLoadingAtom, videoPlayerVisibleAtom } from '../atoms/videoPlayerAtoms';
import { useVideoAudioSync } from '../hooks/useVideoAudioSync';
import { VideoPlayer } from './VideoPlayer';
import { VideoUploadPrompt } from './VideoUploadPrompt';

// Window dimensions and constraints
const DEFAULT_WIDTH = 420;
const DEFAULT_HEIGHT = 336;
const REG_DESKTOP_BREAKPOINT = 1280; // xl Tailwind breakpoint — laptop/regular-desktop
const REG_DESKTOP_DEFAULT_WIDTH = 380;
const REG_DESKTOP_DEFAULT_HEIGHT = 304;
const LARGE_SCREEN_DEFAULT_WIDTH = 560;
const LARGE_SCREEN_DEFAULT_HEIGHT = 448;
const LARGE_SCREEN_BREAKPOINT = 1536;
const MIN_WIDTH = 320;
const MIN_HEIGHT_NO_VIDEO = 240;
const MAX_WIDTH = 1280;
const MAX_HEIGHT = 960;

// Layout measurements
const WINDOW_MARGIN = 20;
const TRACK_PLAYER_HEIGHT = 64;
const SPACING_ABOVE_TRACK_PLAYER = 20;
const HEADER_HEIGHT = 40;
const CONTROLS_HEIGHT = 80;
const CONTENT_PADDING_VERTICAL = 32; // 16px top + 16px bottom (p-4)
const CONTENT_PADDING_HORIZONTAL = 32; // 16px left + 16px right (p-4)
const BOTTOM_PADDING = 20;
// biome-ignore lint/style/noMagicNumbers: <This is literally an aspect ratio not magic>
const ASPECT_RATIO = 16 / 9;

// Calculate minimum height when video is loaded (based on MIN_WIDTH)
// This ensures the video + controls are always visible
const MIN_VIDEO_WIDTH = MIN_WIDTH - CONTENT_PADDING_HORIZONTAL;
const MIN_VIDEO_HEIGHT = MIN_VIDEO_WIDTH / ASPECT_RATIO;
const MIN_HEIGHT_WITH_VIDEO = Math.round(HEADER_HEIGHT + CONTENT_PADDING_VERTICAL + MIN_VIDEO_HEIGHT + CONTROLS_HEIGHT + BOTTOM_PADDING);
const DEFAULT_Y_POSITION = 80;
const RESIZE_HANDLE_MARGIN = 40; // 20px margin on each side
const COMFORTABLE_WIDTH = 420;
const EXPAND_THRESHOLD = 0.8; // 80% threshold for expansion
const FALLBACK_VIEWPORT_WIDTH = 1280;
const ANIMATION_DURATION = 300; // milliseconds
const RESIZE_HANDLE_SIZE = 10; // pixels
const RESIZE_HANDLE_CORNER_SIZE = 20; // pixels
const VIDEO_READY_STATE_THRESHOLD = 1;

// Animation values
const MOTION_SCALE_START = 0.98;
const MOTION_SCALE_BOUNCE = 1.01;
const MOTION_SCALE_END = 1;
const MOTION_DURATION = 0.15; // seconds

/**
 * Resizable and draggable video player window using react-rnd
 * Supports drag from any corner/edge with proper position updates
 */
export const VideoPlayerWindow = () => {
  const isVisible = useAtomValue(videoPlayerVisibleAtom);
  const setVisible = useSetAtom(videoPlayerVisibleAtom);
  const videoFile = useAtomValue(videoFileAtom);
  const isLoading = useAtomValue(videoLoadingAtom);
  const videoElement = useAtomValue(videoElementAtom);
  const clearVideo = useSetAtom(clearVideoAtom);

  // Dynamic minimum height - higher when video is loaded to fit video + controls
  const minHeight = videoFile && !isLoading ? MIN_HEIGHT_WITH_VIDEO : MIN_HEIGHT_NO_VIDEO;

  // Enable audio-video sync
  useVideoAudioSync();

  // Track if we've already resized for the current video to prevent infinite loops
  const hasResizedForVideoRef = useRef<string | null>(null);

  // Load saved position and size from localStorage
  const getSavedState = useCallback(() => {
    if (typeof window === 'undefined') {
      return {
        position: {
          x: WINDOW_MARGIN,
          y: DEFAULT_Y_POSITION
        },
        size: {
          width: DEFAULT_WIDTH,
          height: DEFAULT_HEIGHT
        }
      };
    }
    const isLargeScreen = window.innerWidth > LARGE_SCREEN_BREAKPOINT;
    const isRegDesktop = window.innerWidth >= REG_DESKTOP_BREAKPOINT;
    let defaultSize = {
      width: DEFAULT_WIDTH,
      height: DEFAULT_HEIGHT
    };
    if (isLargeScreen) {
      defaultSize = {
        width: LARGE_SCREEN_DEFAULT_WIDTH,
        height: LARGE_SCREEN_DEFAULT_HEIGHT
      };
    } else if (isRegDesktop) {
      defaultSize = {
        width: REG_DESKTOP_DEFAULT_WIDTH,
        height: REG_DESKTOP_DEFAULT_HEIGHT
      };
    }
    try {
      const savedPosition = localStorage.getItem('video-player-position');
      const savedSize = localStorage.getItem('video-player-size');

      // Calculate bottom-right position if no saved position exists
      const defaultPosition = savedPosition ? JSON.parse(savedPosition) : {
        x: window.innerWidth - defaultSize.width - WINDOW_MARGIN,
        y: window.innerHeight - TRACK_PLAYER_HEIGHT - SPACING_ABOVE_TRACK_PLAYER - defaultSize.height
      };
      return {
        position: defaultPosition,
        size: savedSize ? JSON.parse(savedSize) : defaultSize
      };
    } catch {
      // Calculate bottom-right position as fallback
      return {
        position: {
          x: window.innerWidth - defaultSize.width - WINDOW_MARGIN,
          y: window.innerHeight - TRACK_PLAYER_HEIGHT - SPACING_ABOVE_TRACK_PLAYER - defaultSize.height
        },
        size: defaultSize
      };
    }
  }, []);
  const [state, setState] = useState(getSavedState);

  // Clear video and reset position/size when window is closed
  useEffect(() => {
    if (!isVisible) {
      clearVideo();
      // Reset saved position and size
      if (typeof window !== 'undefined') {
        localStorage.removeItem('video-player-position');
        localStorage.removeItem('video-player-size');
      }
      // Reset state to default position/size
      setState(getSavedState());
      // Reset the resize tracking ref so next video will trigger resize
      hasResizedForVideoRef.current = null;
    }
  }, [isVisible, clearVideo, getSavedState]);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isTopLeftResizing, setIsTopLeftResizing] = useState(false);
  const topLeftResizeStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    bottomRight: {
      x: number;
      y: number;
    };
    size: {
      width: number;
      height: number;
    };
  } | null>(null);

  // Save state to localStorage
  const saveState = useCallback((newPosition?: {
    x: number;
    y: number;
  }, newSize?: {
    width: number;
    height: number;
  }) => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      if (newPosition) {
        localStorage.setItem('video-player-position', JSON.stringify(newPosition));
      }
      if (newSize) {
        localStorage.setItem('video-player-size', JSON.stringify(newSize));
      }
    } catch {
      // Failed to save window state - ignore
    }
  }, []);

  // Constrain window size to fit within viewport
  const constrainSizeToViewport = useCallback((currentSize: {
    width: number;
    height: number;
  }, viewportWidth: number, viewportHeight: number) => {
    let {
      width,
      height
    } = currentSize;
    let changed = false;

    // If window is wider than viewport, shrink width and recalculate height
    if (width > viewportWidth - WINDOW_MARGIN * 2) {
      width = Math.max(MIN_WIDTH, viewportWidth - WINDOW_MARGIN * 2);
      const availableVideoWidth = width - CONTENT_PADDING_HORIZONTAL;
      const videoHeight = availableVideoWidth / ASPECT_RATIO;
      height = Math.round(HEADER_HEIGHT + CONTENT_PADDING_VERTICAL + videoHeight + CONTROLS_HEIGHT + BOTTOM_PADDING);
      changed = true;
    }

    // If window is taller than available space (accounting for player bar), shrink height and recalculate width
    const maxAllowedHeight = viewportHeight - TRACK_PLAYER_HEIGHT - SPACING_ABOVE_TRACK_PLAYER - WINDOW_MARGIN;
    if (height > maxAllowedHeight) {
      height = Math.max(MIN_HEIGHT_NO_VIDEO, maxAllowedHeight);
      const availableVideoHeight = height - HEADER_HEIGHT - CONTENT_PADDING_VERTICAL - CONTROLS_HEIGHT - BOTTOM_PADDING;
      const videoWidth = availableVideoHeight * ASPECT_RATIO;
      width = Math.max(MIN_WIDTH, Math.min(Math.round(videoWidth + CONTENT_PADDING_HORIZONTAL), MAX_WIDTH));
      changed = true;
    }
    return {
      width,
      height,
      changed
    };
  }, []);

  // Constrain position to keep window on screen
  const constrainPositionToViewport = useCallback((currentPosition: {
    x: number;
    y: number;
  }, size: {
    width: number;
    height: number;
  }, viewportWidth: number, viewportHeight: number) => {
    const maxX = Math.max(0, viewportWidth - size.width - WINDOW_MARGIN);
    const maxY = Math.max(0, viewportHeight - TRACK_PLAYER_HEIGHT - SPACING_ABOVE_TRACK_PLAYER - size.height);
    const x = Math.min(Math.max(0, currentPosition.x), maxX);
    const y = Math.min(Math.max(0, currentPosition.y), maxY);
    const changed = x !== currentPosition.x || y !== currentPosition.y;
    return {
      x,
      y,
      changed
    };
  }, []);

  // Keep window on screen and resize if needed when browser is resized
  useEffect(() => {
    if (typeof window === 'undefined' || !isVisible) {
      return;
    }
    const handleWindowResize = () => {
      setState(prev => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const newSize = constrainSizeToViewport(prev.size, viewportWidth, viewportHeight);
        const newPosition = constrainPositionToViewport(prev.position, {
          width: newSize.width,
          height: newSize.height
        }, viewportWidth, viewportHeight);
        if (!(newSize.changed || newPosition.changed)) {
          return prev;
        }
        saveState(newPosition.changed ? {
          x: newPosition.x,
          y: newPosition.y
        } : undefined, newSize.changed ? {
          width: newSize.width,
          height: newSize.height
        } : undefined);
        return {
          position: {
            x: newPosition.x,
            y: newPosition.y
          },
          size: {
            width: newSize.width,
            height: newSize.height
          }
        };
      });
    };
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [isVisible, saveState, constrainSizeToViewport, constrainPositionToViewport]);

  // Auto-resize window when video is loaded
  useEffect(() => {
    if (!(videoElement && videoFile)) {
      return;
    }

    // Use the video URL as a unique identifier
    const videoId = videoFile;

    // Skip if we've already resized for this video
    if (hasResizedForVideoRef.current === videoId) {
      return;
    }
    const handleLoadedMetadata = () => {
      // Skip if we've already resized for this video (double-check inside handler)
      if (hasResizedForVideoRef.current === videoId) {
        return;
      }

      // Get native video dimensions
      const nativeWidth = videoElement.videoWidth;
      const nativeHeight = videoElement.videoHeight;
      if (nativeWidth === 0 || nativeHeight === 0) {
        return;
      }

      // Mark this video as resized
      hasResizedForVideoRef.current = videoId;

      // Trigger resize with animation
      // Also adjust y-position so bottom edge stays in place (window grows upward)
      setIsResizing(true);
      setState(prev => {
        const currentWidth = prev.size.width;
        const availableVideoWidth = currentWidth - CONTENT_PADDING_HORIZONTAL;
        const calculatedVideoHeight = availableVideoWidth / ASPECT_RATIO;

        // Total window height = header + padding + video + controls + bottom padding
        const totalHeight = HEADER_HEIGHT + CONTENT_PADDING_VERTICAL + calculatedVideoHeight + CONTROLS_HEIGHT + BOTTOM_PADDING;

        // Clamp height to bounds
        const optimalHeight = Math.min(Math.max(totalHeight, MIN_HEIGHT_NO_VIDEO), MAX_HEIGHT);
        const newHeight = Math.round(optimalHeight);
        const heightDifference = newHeight - prev.size.height;
        const newY = Math.max(0, prev.position.y - heightDifference);
        const newPosition = {
          x: prev.position.x,
          y: newY
        };
        const newSize = {
          width: currentWidth,
          height: newHeight
        };

        // Save the new size and position
        saveState(newPosition, newSize);
        return {
          position: newPosition,
          size: newSize
        };
      });

      // Reset resizing flag after animation
      setTimeout(() => setIsResizing(false), ANIMATION_DURATION);
    };
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);

    // If metadata is already loaded, trigger immediately
    if (videoElement.readyState >= VIDEO_READY_STATE_THRESHOLD) {
      handleLoadedMetadata();
    }
    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [videoElement, videoFile, saveState]);
  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);
  const handleDragStop = useCallback((_e: unknown, data: {
    x: number;
    y: number;
  }) => {
    setIsDragging(false);
    const newPosition = {
      x: data.x,
      y: data.y
    };
    setState(prev => ({
      ...prev,
      position: newPosition
    }));
    saveState(newPosition);
  }, [saveState]);

  // Custom topLeft resize handlers - bypasses react-rnd for smooth aspect-ratio-locked resizing
  const handleTopLeftResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Store initial state - anchor is bottom-right corner
    topLeftResizeStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      bottomRight: {
        x: state.position.x + state.size.width,
        y: state.position.y + state.size.height
      },
      size: {
        ...state.size
      }
    };
    setIsTopLeftResizing(true);
  }, [state.position, state.size]);
  useEffect(() => {
    if (!isTopLeftResizing) {
      return;
    }
    const handleMouseMove = (e: MouseEvent) => {
      if (!topLeftResizeStartRef.current) {
        return;
      }
      const {
        bottomRight
      } = topLeftResizeStartRef.current;

      // Calculate new width based on mouse position (bottom-right stays fixed)
      let newWidth = bottomRight.x - e.clientX;
      newWidth = Math.max(MIN_WIDTH, Math.min(newWidth, MAX_WIDTH));

      // Calculate height maintaining aspect ratio (only when video loaded)
      let newHeight: number;
      if (videoFile && !isLoading) {
        const availableVideoWidth = newWidth - CONTENT_PADDING_HORIZONTAL;
        const videoHeight = availableVideoWidth / ASPECT_RATIO;
        newHeight = Math.round(HEADER_HEIGHT + CONTENT_PADDING_VERTICAL + videoHeight + CONTROLS_HEIGHT + BOTTOM_PADDING);
        newHeight = Math.max(minHeight, Math.min(newHeight, MAX_HEIGHT));
      } else {
        // No video - allow free height resize
        newHeight = bottomRight.y - e.clientY;
        newHeight = Math.max(minHeight, Math.min(newHeight, MAX_HEIGHT));
      }

      // Calculate new position to keep bottom-right anchored
      const newX = bottomRight.x - newWidth;
      const newY = bottomRight.y - newHeight;
      setState({
        position: {
          x: newX,
          y: newY
        },
        size: {
          width: newWidth,
          height: newHeight
        }
      });
    };
    const handleMouseUp = () => {
      setIsTopLeftResizing(false);
      topLeftResizeStartRef.current = null;
      // Save final state
      saveState(state.position, state.size);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isTopLeftResizing, videoFile, isLoading, minHeight, saveState, state]);

  // Calculate optimal height for a given width
  const calculateHeightForWidth = useCallback((width: number) => {
    const availableVideoWidth = width - CONTENT_PADDING_HORIZONTAL;
    const videoHeight = availableVideoWidth / ASPECT_RATIO;
    return Math.round(HEADER_HEIGHT + CONTENT_PADDING_VERTICAL + videoHeight + CONTROLS_HEIGHT + BOTTOM_PADDING);
  }, []);

  // Calculate optimal width for a given height
  const calculateWidthForHeight = useCallback((height: number) => {
    const availableVideoHeight = height - HEADER_HEIGHT - CONTENT_PADDING_VERTICAL - CONTROLS_HEIGHT - BOTTOM_PADDING;
    const videoWidth = availableVideoHeight * ASPECT_RATIO;
    return Math.round(videoWidth + CONTENT_PADDING_HORIZONTAL);
  }, []);

  // Handle resize to maintain aspect ratio and prevent whitespace (only when video is loaded)
  const handleResize = useCallback(
  // biome-ignore lint/nursery/useMaxParams: Callback signature defined by react-rnd library
  (_e: unknown, direction: string, ref: HTMLElement, _delta: unknown, _position: {
    x: number;
    y: number;
  }) => {
    // Only lock aspect ratio when video is loaded and ready
    if (!(videoFile && !isLoading)) {
      return;
    }
    const currentWidth = ref.offsetWidth;
    const currentHeight = ref.offsetHeight;

    // For horizontal-only resizes (right, left), adjust height to match width
    if (direction === 'right' || direction === 'left') {
      const optimalHeight = calculateHeightForWidth(currentWidth);
      const clampedHeight = Math.max(MIN_HEIGHT_NO_VIDEO, Math.min(optimalHeight, MAX_HEIGHT));
      ref.style.height = `${clampedHeight}px`;
    }
    // For vertical-only resizes (bottom), adjust width to match height
    else if (direction === 'bottom') {
      const optimalWidth = calculateWidthForHeight(currentHeight);
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(optimalWidth, MAX_WIDTH));
      ref.style.width = `${clampedWidth}px`;
    }
    // For corner resizes, prioritize width and adjust height
    else if (direction === 'bottomRight' || direction === 'bottomLeft') {
      const optimalHeight = calculateHeightForWidth(currentWidth);
      const clampedHeight = Math.max(MIN_HEIGHT_NO_VIDEO, Math.min(optimalHeight, MAX_HEIGHT));
      ref.style.height = `${clampedHeight}px`;
    }
  }, [calculateHeightForWidth, calculateWidthForHeight, videoFile, isLoading]);
  const handleResizeStop = useCallback(
  // biome-ignore lint/nursery/useMaxParams: Callback signature defined by react-rnd library
  (_e: unknown, _direction: unknown, ref: HTMLElement, _delta: unknown, position: {
    x: number;
    y: number;
  }) => {
    const newSize = {
      width: ref.offsetWidth,
      height: ref.offsetHeight
    };
    const newPosition = {
      x: position.x,
      y: position.y
    };
    setState({
      position: newPosition,
      size: newSize
    });
    saveState(newPosition, newSize);
  }, [saveState]);

  // Calculate optimal height based on current width
  const calculateOptimalHeight = useCallback(() => {
    const availableVideoWidth = state.size.width - CONTENT_PADDING_HORIZONTAL;
    const videoHeight = availableVideoWidth / ASPECT_RATIO;
    return Math.round(HEADER_HEIGHT + CONTENT_PADDING_VERTICAL + videoHeight + CONTROLS_HEIGHT + BOTTOM_PADDING);
  }, [state.size.width]);

  // Handle double-click on resize handles
  const handleResizeHandleDoubleClick = useCallback((direction: string) => {
    setIsResizing(true);
    if (direction.includes('right') || direction.includes('left')) {
      // Calculate available viewport width with some margin
      const viewportWidth = typeof window === 'undefined' ? FALLBACK_VIEWPORT_WIDTH : window.innerWidth;
      const availableWidth = Math.min(viewportWidth - RESIZE_HANDLE_MARGIN - state.position.x, MAX_WIDTH);

      // If current width is less than threshold of available, expand to fill
      // Otherwise, contract to comfortable size
      const comfortableWidth = viewportWidth > LARGE_SCREEN_BREAKPOINT ? LARGE_SCREEN_DEFAULT_WIDTH : COMFORTABLE_WIDTH;
      const shouldExpand = state.size.width < availableWidth * EXPAND_THRESHOLD;
      const targetWidth = shouldExpand ? availableWidth : comfortableWidth;
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(targetWidth, MAX_WIDTH));

      // Calculate optimal height for the new width to avoid cutting off content
      const availableVideoWidth = clampedWidth - CONTENT_PADDING_HORIZONTAL;
      const videoHeight = availableVideoWidth / ASPECT_RATIO;
      const optimalHeight = Math.round(HEADER_HEIGHT + CONTENT_PADDING_VERTICAL + videoHeight + CONTROLS_HEIGHT + BOTTOM_PADDING);
      const clampedHeight = Math.max(MIN_HEIGHT_NO_VIDEO, Math.min(optimalHeight, MAX_HEIGHT));
      setState(prev => ({
        ...prev,
        size: {
          width: clampedWidth,
          height: clampedHeight
        }
      }));
      saveState(undefined, {
        width: clampedWidth,
        height: clampedHeight
      });
    }
    if (direction.includes('bottom') || direction.includes('top')) {
      // Adjust height to perfectly fit content
      const optimalHeight = calculateOptimalHeight();
      const clampedHeight = Math.max(MIN_HEIGHT_NO_VIDEO, Math.min(optimalHeight, MAX_HEIGHT));
      setState(prev => ({
        ...prev,
        size: {
          width: prev.size.width,
          height: clampedHeight
        }
      }));
      saveState(undefined, {
        width: state.size.width,
        height: clampedHeight
      });
    }
    setTimeout(() => setIsResizing(false), ANIMATION_DURATION);
  }, [state.size, state.position.x, calculateOptimalHeight, saveState]);

  // Custom resize handle component with double-click support
  const createResizeHandle = useCallback((direction: string) => <div onDoubleClick={e => {
    e.preventDefault();
    e.stopPropagation();
    handleResizeHandleDoubleClick(direction);
  }} style={{
    position: 'absolute',
    ...(direction.includes('right') && {
      right: 0,
      top: 0,
      bottom: 0,
      width: `${RESIZE_HANDLE_SIZE}px`,
      cursor: 'ew-resize'
    }),
    ...(direction.includes('left') && {
      left: 0,
      top: 0,
      bottom: 0,
      width: `${RESIZE_HANDLE_SIZE}px`,
      cursor: 'ew-resize'
    }),
    ...(direction.includes('bottom') && !direction.includes('right') && !direction.includes('left') && {
      bottom: 0,
      left: 0,
      right: 0,
      height: `${RESIZE_HANDLE_SIZE}px`,
      cursor: 'ns-resize'
    }),
    ...(direction === 'bottomRight' && {
      right: 0,
      bottom: 0,
      width: `${RESIZE_HANDLE_CORNER_SIZE}px`,
      height: `${RESIZE_HANDLE_CORNER_SIZE}px`,
      cursor: 'nwse-resize'
    }),
    ...(direction === 'bottomLeft' && {
      left: 0,
      bottom: 0,
      width: `${RESIZE_HANDLE_CORNER_SIZE}px`,
      height: `${RESIZE_HANDLE_CORNER_SIZE}px`,
      cursor: 'nesw-resize'
    })
  }} />, [handleResizeHandleDoubleClick]);
  if (!isVisible || typeof window === 'undefined') {
    return null;
  }
  const windowContent = <div className="pointer-events-none fixed inset-0 z-[9997]">
      <Rnd bounds="parent" className="pointer-events-auto" dragHandleClassName="draggable-handle" enableResizing={{
      bottom: true,
      bottomLeft: true,
      bottomRight: true,
      left: true,
      right: true,
      top: false,
      topLeft: false,
      // Handled by custom implementation
      topRight: false
    }} maxHeight={MAX_HEIGHT} maxWidth={MAX_WIDTH} minHeight={minHeight} minWidth={MIN_WIDTH} onDragStart={handleDragStart} onDragStop={handleDragStop} onResize={handleResize} onResizeStop={handleResizeStop} position={state.position} resizeHandleComponent={{
      right: createResizeHandle('right'),
      left: createResizeHandle('left'),
      bottom: createResizeHandle('bottom'),
      bottomRight: createResizeHandle('bottomRight'),
      bottomLeft: createResizeHandle('bottomLeft')
    }} size={state.size} style={{
      transition: isResizing ? 'width 0.3s ease-out, height 0.3s ease-out' : 'none'
    }}>
        {/* Window container */}
        <div className={`h-full overflow-hidden rounded-2xl border border-gray-150 border-solid bg-white shadow-100 dark:border-gray-800 dark:bg-gray-800 ${isDragging || isTopLeftResizing ? 'cursor-grabbing' : ''}`}>
          {/* Custom topLeft resize handle */}
          <div onMouseDown={handleTopLeftResizeStart} style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${RESIZE_HANDLE_CORNER_SIZE}px`,
          height: `${RESIZE_HANDLE_CORNER_SIZE}px`,
          cursor: 'nwse-resize',
          zIndex: 50
        }} />
          {/* Header with title and drag handle */}
          <div className="draggable-handle flex cursor-grab select-none justify-between bg-gray-50 px-2 py-1 active:cursor-grabbing dark:bg-gray-900">
            <div className="flex items-center gap-1 font-semibold text-gray-900 text-sm dark:text-gray-100">
              <GripVertical className="size-5 text-gray-300 dark:text-gray-600" />
              SYNC PREVIEW
            </div>
            {/* Close button */}
            <Button className={cn('h-8 w-8 rounded-full')} icon onClick={() => setVisible(false)} size="sm" variant="transparent">
              <X className="size-full fill-none p-1 text-gray-950 dark:text-white" />
            </Button>
          </div>

          {/* Content area */}
          <div className="@container flex flex-col overflow-hidden p-4 pb-5 dark:bg-gray-950" style={{
          height: `calc(100% - ${HEADER_HEIGHT}px)`
        }}>
            {/* Hidden VideoPlayer to load/validate video while showing upload screen */}
            {/** biome-ignore lint/nursery/noLeakedRender: <Needed to load video in background> */}
            {videoFile && isLoading && <div className="hidden">
                <VideoPlayer />
              </div>}

            <AnimatePresence mode="wait">
              {/** biome-ignore lint/nursery/noLeakedRender: <Conditional rendering> */}
              {videoFile && !isLoading ? <motion.div animate={{
              opacity: 1,
              scale: [MOTION_SCALE_START, MOTION_SCALE_BOUNCE, MOTION_SCALE_END]
            }} className="size-full" exit={{
              opacity: 0,
              scale: MOTION_SCALE_START
            }} initial={{
              opacity: 0,
              scale: MOTION_SCALE_START
            }} key="video-player" transition={{
              duration: MOTION_DURATION,
              ease: 'easeOut'
            }}>
                  <VideoPlayer />
                </motion.div> : <motion.div animate={{
              opacity: 1,
              scale: [MOTION_SCALE_START, MOTION_SCALE_BOUNCE, MOTION_SCALE_END]
            }} className="size-full" exit={{
              opacity: 0,
              scale: MOTION_SCALE_START
            }} initial={{
              opacity: 0,
              scale: MOTION_SCALE_START
            }} key="video-upload" transition={{
              duration: MOTION_DURATION,
              ease: 'easeOut'
            }}>
                  <VideoUploadPrompt />
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </Rnd>
    </div>;
  return createPortal(windowContent, document.body);
};